import { deserialize } from './utils/deserialize'
import { applyState } from '../store/utils/apply-state'
import { exceptions } from '../utils/exceptions'
import type { Store } from '../store'

export interface BootstrapData {
  featureFlags: string[]
  featureGates: Record<string, boolean>
  orgLimits: Record<string, unknown>
  data: unknown[]
  included: unknown[]
  userId: string
  orgId: string | null
}

const isDevelopment = process.env.NODE_ENV === 'development'

/**
 * Logs bootstrap information only in development
 */
const logBootstrap = (message: string, data?: unknown) => {
  if (isDevelopment) {
    console.log(`[Bootstrap] ${message}`, data || '')
  }
}

/**
 * Logs bootstrap errors (always logged, but with more detail in development)
 */
const logBootstrapError = (message: string, error?: unknown) => {
  if (isDevelopment) {
    console.error(`[Bootstrap] ${message}`, error)
  } else {
    console.error(`[Bootstrap] ${message}`)
  }
}

/**
 * Fetches bootstrap data from the API with performance monitoring
 */
export const fetchBootstrap = async (): Promise<BootstrapData> => {
  const startTime = performance.now()
  logBootstrap('Fetching bootstrap data from /api/v1/bootstrap')

  // Add breadcrumb for bootstrap fetch start
  exceptions.addBreadcrumb({
    action: 'Bootstrap fetch started',
    category: 'Bootstrap',
    metadata: {
      timestamp: new Date().toISOString(),
    },
  })

  // Include credentials to ensure cookies (session) are sent with the request
  // This is required for authentication via warden (session-based auth)
  // Add a timeout to prevent hanging indefinitely (30 seconds)
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout

  let response: Response
  try {
    response = await fetch('/api/v1/bootstrap', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      signal: controller.signal,
    })
    clearTimeout(timeoutId)
  } catch (error: unknown) {
    clearTimeout(timeoutId)
    const err = error as { name?: string }
    if (err.name === 'AbortError') {
      logBootstrapError('Bootstrap fetch timed out after 30 seconds')
      const timeoutError = new Error(
        'Bootstrap request timed out. Please try again.',
      )
      ;(timeoutError as Error & { status?: number }).status = 408
      exceptions.handleCustom('BootstrapFetchTimeout', {
        message: 'Bootstrap fetch timed out',
        params: {
          timeout: '30s',
        },
        tags: {
          error_type: 'bootstrap_fetch_timeout',
        },
      })
      throw timeoutError
    }
    throw error
  }

  const fetchTime = performance.now() - startTime
  logBootstrap(`Response received in ${fetchTime.toFixed(2)}ms`, {
    status: response.status,
    statusText: response.statusText,
  })

  // Add breadcrumb for response received
  exceptions.addBreadcrumb({
    action: 'Bootstrap fetch response',
    category: 'Bootstrap',
    metadata: {
      status: response.status,
      statusText: response.statusText,
      fetchTime: `${fetchTime.toFixed(2)}ms`,
    },
  })

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error')
    logBootstrapError(
      `Failed to fetch bootstrap data: ${response.status} ${response.statusText}`,
      isDevelopment ? errorText.substring(0, 500) : undefined,
    )

    // Create more specific error messages
    let errorMessage = 'Failed to load application data'
    if (response.status === 401) {
      errorMessage = 'Your session has expired. Please refresh the page.'
    } else if (response.status === 500) {
      errorMessage =
        'Server error occurred. Please try again or contact support.'
    } else if (response.status >= 400) {
      errorMessage = `Unable to load application (${response.status}). Please try again.`
    }

    const error = new Error(errorMessage) as Error & { status?: number }
    error.status = response.status

    exceptions.handleCustom('BootstrapFetchError', {
      message: errorMessage,
      params: {
        status: response.status,
        statusText: response.statusText,
        errorText: errorText.substring(0, 1000), // Limit error text size
        fetchTime: `${fetchTime.toFixed(2)}ms`,
      },
      tags: {
        error_type: 'bootstrap_fetch',
        http_status: response.status.toString(),
      },
    })

    throw error
  }

  const parseStartTime = performance.now()
  const data = await response.json()
  const parseTime = performance.now() - parseStartTime
  const totalTime = performance.now() - startTime

  logBootstrap('Bootstrap data received', {
    featureFlags: data.featureFlags?.length,
    featureGates: Object.keys(data.featureGates || {}).length,
    userId: data.userId,
    orgId: data.orgId,
    dataCount: data.data?.length,
    includedCount: data.included?.length,
    fetchTime: `${fetchTime.toFixed(2)}ms`,
    parseTime: `${parseTime.toFixed(2)}ms`,
    totalTime: `${totalTime.toFixed(2)}ms`,
  })

  // Add breadcrumb for successful bootstrap fetch
  exceptions.addBreadcrumb({
    action: 'Bootstrap data received',
    category: 'Bootstrap',
    metadata: {
      userId: data.userId,
      orgId: data.orgId,
      dataCount: data.data?.length,
      includedCount: data.included?.length,
      totalTime: `${totalTime.toFixed(2)}ms`,
    },
  })

  // Track performance metrics for large orgs
  if (data.data?.length > 100 || data.included?.length > 200) {
    logBootstrap('Large org detected - consider optimizing bootstrap payload', {
      dataCount: data.data?.length,
      includedCount: data.included?.length,
    })

    // Add breadcrumb for large org detection
    exceptions.addBreadcrumb({
      action: 'Large org detected',
      category: 'Bootstrap',
      metadata: {
        dataCount: data.data?.length,
        includedCount: data.included?.length,
        userId: data.userId,
        orgId: data.orgId,
      },
    })
  }

  return data
}

/**
 * Loads bootstrap data into the store with performance monitoring
 */
export const loadBootstrapIntoStore = async (
  store: Store,
): Promise<BootstrapData> => {
  const loadStartTime = performance.now()

  try {
    const bootstrapData = await fetchBootstrap()
    const fetchTime = performance.now() - loadStartTime

    // Load feature flags
    if (bootstrapData.featureFlags?.length > 0) {
      store.featureFlags.load(bootstrapData.featureFlags)
    }

    // Load feature gates
    if (
      bootstrapData.featureGates &&
      Object.keys(bootstrapData.featureGates).length > 0
    ) {
      store.featureGates.load(bootstrapData.featureGates)
    }

    // Load org limits
    if (
      bootstrapData.orgLimits &&
      Object.keys(bootstrapData.orgLimits).length > 0
    ) {
      store.orgLimits.load(bootstrapData.orgLimits as Record<string, number>)
    }

    // Deserialize and load JSON:API data
    const deserializeStartTime = performance.now()
    const jsonApiDoc = {
      data: bootstrapData.data,
      included: bootstrapData.included,
    }

    const deserialized = deserialize(jsonApiDoc)
    const deserializeTime = performance.now() - deserializeStartTime

    if (deserialized && deserialized.data) {
      // Verify user data exists in deserialized data before applying state
      // This ensures the reference can resolve immediately
      const userDataExists =
        deserialized.data.users && deserialized.data.users[bootstrapData.userId]

      if (!userDataExists && bootstrapData.userId) {
        logBootstrapError(
          `User data for ${bootstrapData.userId} not found in deserialized data`,
          `Available users: ${Object.keys(deserialized.data.users || {}).join(
            ', ',
          )}`,
        )
      }

      // Load all resources and set currentUser atomically in a single snapshot.
      // This ensures the user data exists AND the currentUser reference is set
      // before any reactive code can access nonNullCurrentUser.
      const applyStartTime = performance.now()
      applyState(store, deserialized.data, bootstrapData.userId || null)
      const applyTime = performance.now() - applyStartTime

      const totalLoadTime = performance.now() - loadStartTime
      logBootstrap('Bootstrap data loaded into store', {
        fetchTime: `${fetchTime.toFixed(2)}ms`,
        deserializeTime: `${deserializeTime.toFixed(2)}ms`,
        applyTime: `${applyTime.toFixed(2)}ms`,
        totalLoadTime: `${totalLoadTime.toFixed(2)}ms`,
      })

      // Add breadcrumb for successful bootstrap load
      const breadcrumbMetadata: Record<string, string | number> = {
        userId: bootstrapData.userId,
        totalLoadTime: parseFloat(totalLoadTime.toFixed(2)),
      }
      if (bootstrapData.orgId) {
        breadcrumbMetadata.orgId = bootstrapData.orgId
      }
      exceptions.addBreadcrumb({
        action: 'Bootstrap data loaded into store',
        category: 'Bootstrap',
        metadata: breadcrumbMetadata,
      })
    } else if (bootstrapData.userId) {
      // Fallback: set user even if no data was loaded
      store.setCurrentUser(bootstrapData.userId)
    }

    return bootstrapData
  } catch (error) {
    const errorTime = performance.now() - loadStartTime
    logBootstrapError(
      `Failed to load bootstrap data after ${errorTime.toFixed(2)}ms`,
      error,
    )

    const errorObj = error instanceof Error ? error : new Error(String(error))
    exceptions.handleCustom('BootstrapLoadError', {
      message: errorObj.message,
      params: {
        errorName: errorObj.name,
        errorStack: errorObj.stack,
        loadTime: `${errorTime.toFixed(2)}ms`,
      },
      tags: {
        error_type: 'bootstrap_load',
      },
    })

    throw error
  }
}
